import type { Choice, Variable } from './types'
import { uid } from './store'

const N = (mu: number, sigma: number) => ({ kind: 'Normal' as const, params: { mu, sigma } })
const B = (p: number) => ({ kind: 'Bernoulli' as const, params: { p } })
// Log-space params: median e^mu. For costs that are usually small, occasionally big.
const LN = (mu: number, sigma: number) => ({ kind: 'LogNormal' as const, params: { mu, sigma } })

// How the engine reads these (backend/server.py simulate_choice), which every
// template below must respect:
// - 'risk' is a bad event: in a year it fires it wipes 60% of that year's
//   income (costs stay). So it only belongs on options with income (on a
//   costs-only option it has nothing to hit), never on a good event, and it
//   fires independently every year ("first year only" isn't possible).
//   Occasional costs are a right-skewed cost (LN) instead; good events an
//   expected value.
// - A job loss costs far less than 60% of a year on average (unemployment
//   benefit, ~4–6 month spells), so layoff chances are calibrated to the
//   expected loss: LAYOFF_P (see the sourced new-job page, explainerSeeds.ts).
// - 'once' lands in year 1; there is no payout at the end of the horizon, so
//   assets you keep (a flat, a car) are costed by what you lose, not what you pay.
// - A salary that starts later (after a degree) is booked from year 1, with the
//   years without it as an up-front cost of that same salary.
const LAYOFF_P = 0.02

const v = (
  name: string,
  role: Variable['role'],
  time: Variable['time'],
  units: string,
  dist: any,
  type: Variable['type'] = 'parameter',
): Variable => ({
  id: uid(),
  name,
  type,
  role,
  time,
  units,
  dist,
})

export interface DecisionTemplate {
  key: string
  label: string
  emoji: string
  description: string
  category: 'careers' | 'moves' | 'education' | 'money' | 'life'
  horizon_years: number
  choices: Omit<Choice, 'id'>[]
}

// All amounts in your base currency (converted to your locale via FX rates).
export const TEMPLATES: DecisionTemplate[] = [
  {
    key: 'job',
    label: 'Take the new job?',
    emoji: '💼',
    description: 'Compare your current role against a fresh offer.',
    category: 'careers',
    horizon_years: 5,
    choices: [
      {
        name: 'Stay at current job',
        notes: 'Stable, known, raises in line with the market.',
        variables: [
          v('Gross salary', 'income', 'per_step', '/year', N(52000, 2000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(2200, 600)),
          v('Layoff risk', 'risk', 'per_step', 'chance/year', B(LAYOFF_P), 'shock'),
        ],
      },
      {
        name: 'Take new offer',
        notes: 'A +10% offer and a sign-on bonus; new hires are easier to let go.',
        variables: [
          v('Gross salary', 'income', 'per_step', '/year', N(57000, 2000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(2400, 700)),
          v('Sign-on bonus', 'one_time_income', 'once', '', N(5000, 1500)),
          v('Layoff risk (new hire)', 'risk', 'per_step', 'chance/year', B(0.03), 'shock'),
        ],
      },
    ],
  },
  {
    key: 'move',
    label: 'Move cities?',
    emoji: '🏙️',
    description: 'Cost of living, salary, and lifestyle trade-offs between two cities.',
    category: 'moves',
    horizon_years: 3,
    choices: [
      {
        name: 'Stay in current city',
        notes: 'Established network, known costs, no disruption.',
        variables: [
          v('Net salary', 'income', 'per_step', '/year', N(62000, 4500)),
          v('Rent', 'cost', 'per_step', '/month', N(1900, 200)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7, 1)),
        ],
      },
      {
        name: 'Move to new city',
        notes: 'Lower cost of living, weather upgrade, rebuild network.',
        variables: [
          v('Net salary', 'income', 'per_step', '/year', N(48000, 4000)),
          v('Rent', 'cost', 'per_step', '/month', N(1100, 150)),
          v('Moving costs', 'one_time_cost', 'once', '', N(4500, 1200)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8, 1.4)),
        ],
      },
    ],
  },
  {
    key: 'masters',
    label: 'Do a Master\'s degree?',
    emoji: '🎓',
    description: 'Is the salary premium worth 2 years and the tuition cost?',
    category: 'education',
    horizon_years: 8,
    choices: [
      {
        name: 'Keep working',
        notes: 'Steady experience, no career pause.',
        variables: [
          v('Gross salary', 'income', 'per_step', '/year', N(55000, 4000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(2200, 900)),
        ],
      },
      {
        name: 'Do a 2-year Master\'s',
        notes: 'Two years without salary, then ~13% more (German graduate data). The study years are booked up front as two years of post-degree pay.',
        variables: [
          v('Tuition + fees', 'one_time_cost', 'once', '', N(8000, 4000)),
          v('Two years without salary', 'one_time_cost', 'once', '', N(124000, 12000)),
          v('Post-degree salary (+13%)', 'income', 'per_step', '/year', N(62000, 7000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(2500, 900)),
        ],
      },
    ],
  },
  {
    key: 'startup',
    label: 'Quit to start a startup?',
    emoji: '🚀',
    description: 'Stable big-tech salary vs. the founder lottery.',
    category: 'careers',
    horizon_years: 5,
    choices: [
      {
        name: 'Stay at big tech',
        notes: 'Predictable comp, equity vesting, golden handcuffs.',
        variables: [
          v('Total comp', 'income', 'per_step', '/year', N(110000, 12000)),
          v('Vesting raise', 'income', 'cumulative', '/year', N(4500, 1800)),
          v('Layoff risk', 'risk', 'per_step', 'chance/year', B(0.03), 'shock'),
        ],
      },
      {
        name: 'Bootstrap startup',
        notes: 'Low founder pay; your equity is usually worth little, occasionally a lot. Booked in year 1, so it is optimistic on timing.',
        variables: [
          v('Founder salary', 'income', 'per_step', '/year', N(28000, 14000)),
          v('Savings burn', 'one_time_cost', 'once', '', N(50000, 12000)),
          v('Your equity at exit (median ~3K, mean ~70K)', 'one_time_income', 'once', '', LN(8, 2.5)),
        ],
      },
    ],
  },
  {
    key: 'buy-vs-rent',
    label: 'Buy a flat or keep renting?',
    emoji: '🏡',
    description: 'Mortgage math vs. flexibility — wherever you live.',
    category: 'money',
    horizon_years: 10,
    choices: [
      {
        name: 'Keep renting',
        notes: 'Rent a comparable flat and keep the deposit invested.',
        variables: [
          v('Rent (price ÷ ~24 years of rent)', 'cost', 'per_step', '/month', N(1370, 70)),
          v('Rent increase (~2%/year)', 'cost', 'cumulative', '/year', N(345, 150)),
          v('Return on the 80K deposit (~5%)', 'income', 'per_step', '/year', N(4000, 9600)),
        ],
      },
      {
        name: 'Buy a home',
        notes: '400K flat, 20% down. Repayments build equity, so only interest, upkeep and purchase costs are lost.',
        variables: [
          v('Purchase costs (tax, notary, agent ~11%)', 'one_time_cost', 'once', '', N(44000, 8000)),
          v('Mortgage interest (~4% on a falling 320K)', 'cost', 'per_step', '/year', N(11400, 800)),
          v('Upkeep + property tax', 'cost', 'per_step', '/month', N(320, 70)),
          v('House price change (~3%/year)', 'income', 'per_step', '/year', N(12000, 24000)),
        ],
      },
    ],
  },
  {
    key: 'kids',
    label: 'Have a kid (now)?',
    emoji: '👶',
    description: 'Childcare costs, parental leave, and the wellbeing trade-off.',
    category: 'life',
    horizon_years: 5,
    choices: [
      {
        name: 'Wait 3 more years',
        notes: 'More freedom now; the money you don\'t spend on a child is the difference below.',
        variables: [
          v('Lifestyle wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7.5, 1.2)),
        ],
      },
      {
        name: 'Have a baby now',
        notes: 'Childcare costs and parental leave vary widely by country and employer.',
        variables: [
          v('Childcare costs', 'cost', 'per_step', '/year', N(6500, 2500)),
          v('Other kid expenses', 'cost', 'per_step', '/year', N(4000, 1500)),
          v('First-year one-off (gear + hospital)', 'one_time_cost', 'once', '', N(3500, 1500)),
          v('Income lost to parental leave', 'one_time_cost', 'once', '', N(12000, 6000)),
          v('Parental wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8.5, 2)),
        ],
      },
    ],
  },
  {
    key: 'bootcamp',
    label: 'Coding bootcamp or self-teach?',
    emoji: '💻',
    description: 'Pay for speed and structure, or grind GitHub for free.',
    category: 'education',
    horizon_years: 4,
    choices: [
      {
        name: 'Self-teach (12 months)',
        notes: 'Free, slower, no credential, no network.',
        variables: [
          v('Opportunity cost', 'one_time_cost', 'once', '', N(28000, 8000)),
          v('Junior developer salary', 'income', 'per_step', '/year', N(42000, 8000)),
          v('Longer job search (expected)', 'one_time_cost', 'once', '', N(6000, 3000)),
        ],
      },
      {
        name: 'Bootcamp (4 months)',
        notes: 'Fast, structured, career services. Graduates restart as juniors (Le Wagon Germany: median ~44K).',
        variables: [
          v('Tuition', 'one_time_cost', 'once', '', N(9000, 1500)),
          v('Opportunity cost', 'one_time_cost', 'once', '', N(12000, 3000)),
          v('Junior developer salary', 'income', 'per_step', '/year', N(44000, 6000)),
          v('Longer job search (expected)', 'one_time_cost', 'once', '', N(2500, 1500)),
        ],
      },
    ],
  },
  {
    key: 'remote',
    label: 'Go fully remote or hybrid?',
    emoji: '🌴',
    description: 'Office visibility vs. async focus + commute savings.',
    category: 'careers',
    horizon_years: 3,
    choices: [
      {
        name: 'Hybrid (3 days office)',
        notes: 'Office visibility, structured days, longer commute.',
        variables: [
          v('Gross salary', 'income', 'per_step', '/year', N(72000, 6000)),
          v('Commute + lunch cost', 'cost', 'per_step', '/year', N(2800, 500)),
          v('Promotion raises (22%/year chance of ~8K)', 'income', 'cumulative', '/year', N(1760, 800)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7, 1)),
        ],
      },
      {
        name: 'Fully remote',
        notes: 'No commute, less visible, async culture. Check tax-residency rules if moving abroad.',
        variables: [
          v('Gross salary', 'income', 'per_step', '/year', N(66000, 7000)),
          v('Home office setup', 'one_time_cost', 'once', '', N(2500, 600)),
          v('Promotion raises (15%/year chance of ~8K)', 'income', 'cumulative', '/year', N(1200, 700)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8.2, 1.2)),
        ],
      },
    ],
  },
  {
    key: 'fire',
    label: 'Pursue FIRE or live more now?',
    emoji: '🔥',
    description: 'Save 50% and retire at 45, or enjoy a more balanced life.',
    category: 'money',
    horizon_years: 15,
    choices: [
      {
        name: 'Balanced spending (save 25%)',
        notes: 'Travel, hobbies, a slower but sustainable wealth build.',
        variables: [
          v('Annual savings', 'income', 'per_step', '/year', N(15000, 4000)),
          v('Investment return (~5% on each year\'s savings, compounding)', 'income', 'cumulative', '/year', N(750, 1000)),
          v('Lifestyle wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8, 1)),
        ],
      },
      {
        name: 'Aggressive FIRE (save 55%)',
        notes: 'Frugal, focused, exit-velocity by year 12.',
        variables: [
          v('Annual savings', 'income', 'per_step', '/year', N(38000, 6000)),
          v('Investment return (~5% on each year\'s savings, compounding)', 'income', 'cumulative', '/year', N(1900, 2500)),
          v('Lifestyle wellbeing', 'wellbeing', 'per_step', 'pts/10', N(6.5, 1.5)),
          v('Burnout risk', 'risk', 'per_step', 'chance/year', B(0.10), 'shock'),
        ],
      },
    ],
  },
  {
    key: 'pet',
    label: 'Get a dog?',
    emoji: '🐕',
    description: 'Adoption fees, vet bills, and 10 years of daily companionship.',
    category: 'life',
    horizon_years: 10,
    choices: [
      {
        name: 'Stay pet-free',
        notes: 'Lower costs, more travel flexibility.',
        variables: [
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7.2, 1)),
        ],
      },
      {
        name: 'Adopt a dog',
        notes: 'Food, vet, licensing, the odd emergency, daily companionship.',
        variables: [
          v('Adoption + setup', 'one_time_cost', 'once', '', N(900, 250)),
          v('Annual costs (food + vet + license)', 'cost', 'per_step', '/year', N(1800, 450)),
          v('Emergency vet bills (most years small, some 1K+)', 'cost', 'per_step', '/year', LN(5.0, 1.2)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8.8, 1.4)),
        ],
      },
    ],
  },
  {
    key: 'freelance',
    label: 'Freelance or stay employed?',
    emoji: '🧑‍💻',
    description: 'Trade job security for flexibility and a higher day rate — both before income tax.',
    category: 'careers',
    horizon_years: 3,
    choices: [
      {
        name: 'Stay full-time employed',
        notes: 'Your employer pays half of every social contribution and keeps paying when you are ill or on holiday.',
        variables: [
          v('Gross salary', 'income', 'per_step', '/year', N(63000, 4000)),
          v("Employer's half of pension contributions", 'income', 'per_step', '/year', N(5900, 300)),
          v('Your health, care + unemployment insurance', 'cost', 'per_step', '/year', N(7700, 300)),
          v('Annual raise', 'income', 'cumulative', '/year', N(2600, 700)),
          v('Layoff risk', 'risk', 'per_step', 'chance/year', B(LAYOFF_P), 'shock'),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7, 1.2)),
        ],
      },
      {
        name: 'Go freelance',
        notes: 'Higher hourly rate, but unbilled gaps, your own costs, and both halves of health insurance.',
        variables: [
          v('Revenue (billed hours × rate)', 'income', 'per_step', '/year', N(72000, 20000)),
          v('Business costs', 'cost', 'per_step', '/year', N(9400, 2000)),
          v('Health + care insurance, both halves', 'cost', 'per_step', '/year', N(13500, 1500)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7.8, 1.5)),
        ],
      },
    ],
  },
  {
    key: 'promotion',
    label: 'Take the promotion to manager?',
    emoji: '📈',
    description: 'Pay bump and title vs. staying in the craft as an IC.',
    category: 'careers',
    horizon_years: 4,
    choices: [
      {
        name: 'Stay as individual contributor',
        notes: 'Deep expertise, no people overhead, IC promotion path.',
        variables: [
          v('Total comp', 'income', 'per_step', '/year', N(95000, 8000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(4000, 1500)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7.5, 1)),
        ],
      },
      {
        name: 'Become a manager',
        notes: 'Pay bump, but meetings, performance reviews, and team risk.',
        variables: [
          v('Total comp', 'income', 'per_step', '/year', N(115000, 10000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(5500, 2000)),
          v('Role doesn\'t work out (exit or step back)', 'risk', 'per_step', 'chance/year', B(0.03), 'shock'),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(6.5, 1.5)),
        ],
      },
    ],
  },
  {
    key: 'raise-vs-hop',
    label: 'Negotiate a raise or job-hop?',
    emoji: '💰',
    description: 'Internal loyalty premium vs. the switching salary bump.',
    category: 'careers',
    horizon_years: 3,
    choices: [
      {
        name: 'Negotiate a raise',
        notes: 'Lower risk, preserves relationships, smaller bump.',
        variables: [
          v('Current salary', 'income', 'per_step', '/year', N(72000, 3000)),
          v('Raise (70% chance of ~5K, kept every year)', 'income', 'per_step', '/year', N(3500, 1500)),
          v('Annual raise going forward', 'income', 'cumulative', '/year', N(2500, 1000)),
          v('Layoff risk', 'risk', 'per_step', 'chance/year', B(LAYOFF_P), 'shock'),
        ],
      },
      {
        name: 'Switch jobs',
        notes: 'Bigger jump (+10% here), but onboarding cost and cultural unknown.',
        variables: [
          v('New salary', 'income', 'per_step', '/year', N(79000, 4000)),
          v('Job search cost (time + stress)', 'one_time_cost', 'once', '', N(3000, 1500)),
          v('Layoff risk (new hire)', 'risk', 'per_step', 'chance/year', B(0.03), 'shock'),
          v('Annual raise', 'income', 'cumulative', '/year', N(2700, 1000)),
        ],
      },
    ],
  },
  {
    key: 'sabbatical',
    label: 'Take a sabbatical?',
    emoji: '🌿',
    description: 'Burn savings for a break — is the wellbeing recharge worth it?',
    category: 'careers',
    horizon_years: 5,
    choices: [
      {
        name: 'Keep working',
        notes: 'No income gap, steady compounding, growing burnout risk.',
        variables: [
          v('Annual salary', 'income', 'per_step', '/year', N(80000, 6000)),
          v('Burnout risk', 'risk', 'per_step', 'chance/year', B(0.05), 'shock'),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(6.5, 1.2)),
        ],
      },
      {
        name: 'Take a 6-month sabbatical',
        notes: 'Six months without pay (you pay your living costs either way) and some re-entry uncertainty.',
        variables: [
          v('Six months without salary', 'one_time_cost', 'once', '', N(40000, 3000)),
          v('Re-entry salary (slight dip)', 'income', 'per_step', '/year', N(76000, 8000)),
          v('Longer job search after the break (expected)', 'one_time_cost', 'once', '', N(6000, 4000)),
          v('Wellbeing post-break', 'wellbeing', 'per_step', 'pts/10', N(8.5, 1)),
        ],
      },
    ],
  },
  {
    key: 'debt-vs-invest',
    label: 'Pay off debt early or invest?',
    emoji: '⚖️',
    description: 'Guaranteed interest saved vs. the expected market return on the same money.',
    category: 'money',
    horizon_years: 7,
    choices: [
      {
        name: 'Pay off debt first',
        notes: 'Use 60K to clear a 5% loan: a guaranteed 5% return.',
        variables: [
          v('Interest no longer paid (5% on 60K)', 'income', 'per_step', '/year', N(3000, 100)),
          v('Wellbeing (stress-free)', 'wellbeing', 'per_step', 'pts/10', N(8, 0.8)),
        ],
      },
      {
        name: 'Invest immediately',
        notes: 'Invest the same 60K and keep paying the loan: ~7% expected, ~17% swings a year.',
        variables: [
          v('Market return on 60K (~7% expected)', 'income', 'per_step', '/year', N(4200, 10200)),
          v('Wellbeing (carrying debt)', 'wellbeing', 'per_step', 'pts/10', N(6.5, 1)),
        ],
      },
    ],
  },
  {
    key: 'car',
    label: 'Buy, lease, or ditch the car?',
    emoji: '🚗',
    description: 'Ownership equity vs. lease flexibility vs. going car-free.',
    category: 'money',
    horizon_years: 5,
    choices: [
      {
        name: 'Buy a used car',
        notes: 'You get most of the price back when you sell, so the cost is depreciation, upkeep and the money tied up.',
        variables: [
          v('Depreciation loss', 'cost', 'per_step', '/year', N(1800, 600)),
          v('Interest lost on the 14K price (~4%)', 'cost', 'per_step', '/year', N(560, 100)),
          v('Insurance + tax + maintenance', 'cost', 'per_step', '/year', N(2400, 500)),
          v('Repairs (most years small, some big)', 'cost', 'per_step', '/year', LN(6.5, 0.8)),
        ],
      },
      {
        name: 'Lease a new car',
        notes: 'Predictable monthly cost, always under warranty, no residual risk.',
        variables: [
          v('Monthly lease payment', 'cost', 'per_step', '/month', N(420, 60)),
          v('Insurance + tax', 'cost', 'per_step', '/year', N(1400, 300)),
          v('Excess-mileage + return charges', 'cost', 'per_step', '/year', LN(5.3, 0.8)),
        ],
      },
      {
        name: 'No car (transit + rental)',
        notes: 'No ownership cost — public transport, cycling, occasional car rental.',
        variables: [
          v('Annual transport cost', 'cost', 'per_step', '/year', N(1800, 400)),
          v('Occasional car rental', 'cost', 'per_step', '/year', N(600, 300)),
          v('Wellbeing (convenience trade-off)', 'wellbeing', 'per_step', 'pts/10', N(7, 1.5)),
        ],
      },
    ],
  },
  {
    key: 'lump-sum',
    label: 'Lump sum or dollar-cost average?',
    emoji: '📊',
    description: 'Time in the market vs. a smoother entry: only the first year differs.',
    category: 'money',
    horizon_years: 1,
    choices: [
      {
        name: 'Invest all now (lump sum)',
        notes: 'All 50K in the market for the whole first year. After year 1 both are fully invested.',
        variables: [
          v('Market return on 50K (~7% expected, ~17% swings)', 'income', 'per_step', '/year', N(3500, 8500)),
        ],
      },
      {
        name: 'Spread over 12 months',
        notes: 'About half the money is invested on average in year 1; the rest waits in cash.',
        variables: [
          v('Market return on the ~25K invested on average', 'income', 'per_step', '/year', N(1750, 4250)),
          v('Interest on cash waiting (~2.3%)', 'income', 'per_step', '/year', N(575, 50)),
        ],
      },
    ],
  },
  {
    key: 'phd',
    label: 'Do a PhD?',
    emoji: '🔬',
    description: '4–5 years of stipend vs. industry salary — plus the career ceiling question.',
    category: 'education',
    horizon_years: 20,
    choices: [
      {
        name: 'Work in industry',
        notes: 'Higher pay from day one, faster career progression in most fields.',
        variables: [
          v('Starting salary', 'income', 'per_step', '/year', N(65000, 6000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(3000, 1200)),
          v('Layoff risk', 'risk', 'per_step', 'chance/year', B(LAYOFF_P), 'shock'),
        ],
      },
      {
        name: 'Do a PhD (4–5 years)',
        notes: 'Stipend now, higher ceiling later. The PhD years are booked up front as the gap between stipend and post-PhD pay; ~1 in 5 don\'t finish.',
        variables: [
          v('PhD years: stipend instead of salary (4.5 × (80K − 22K))', 'one_time_cost', 'once', '', N(261000, 25000)),
          v('Post-PhD salary', 'income', 'per_step', '/year', N(80000, 15000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(3000, 1200)),
        ],
      },
    ],
  },
  {
    key: 'mba',
    label: 'Get an MBA?',
    emoji: '🎯',
    description: 'Is the network and salary premium worth 2 years and the tuition bill?',
    category: 'education',
    horizon_years: 7,
    choices: [
      {
        name: 'Keep working',
        notes: 'No tuition debt, continuous experience, slower ceiling in some paths.',
        variables: [
          v('Salary', 'income', 'per_step', '/year', N(75000, 6000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(3500, 1200)),
        ],
      },
      {
        name: 'Full-time MBA (2 years)',
        notes: 'Tuition plus two years without pay (living costs you pay either way), booked up front as two years of post-MBA pay.',
        variables: [
          v('Tuition', 'one_time_cost', 'once', '', N(90000, 30000)),
          v('Two years without salary', 'one_time_cost', 'once', '', N(220000, 30000)),
          v('Post-MBA salary', 'income', 'per_step', '/year', N(110000, 20000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(6000, 2500)),
        ],
      },
    ],
  },
  {
    key: 'part-time',
    label: 'Go part-time for family?',
    emoji: '👨‍👩‍👧',
    description: 'Trade income for time — model the financial and wellbeing gap.',
    category: 'life',
    horizon_years: 4,
    choices: [
      {
        name: 'Stay full-time',
        notes: 'Full salary and progression, less time for family.',
        variables: [
          v('Annual salary', 'income', 'per_step', '/year', N(80000, 6000)),
          v('Annual raise', 'income', 'cumulative', '/year', N(3000, 1000)),
          v('Wellbeing (work-life balance)', 'wellbeing', 'per_step', 'pts/10', N(6.5, 1.2)),
        ],
      },
      {
        name: 'Go 4-day week',
        notes: '20% pay cut and slower progression, but significant time gain.',
        variables: [
          v('Reduced salary (80%)', 'income', 'per_step', '/year', N(64000, 5000)),
          v('Childcare savings', 'income', 'per_step', '/year', N(5000, 1500)),
          v('Annual raise (slower progression)', 'income', 'cumulative', '/year', N(1800, 800)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8.5, 1)),
        ],
      },
    ],
  },
  {
    key: 'roommates',
    label: 'Rent alone or with roommates?',
    emoji: '🏠',
    description: 'Privacy premium vs. splitting the bill.',
    category: 'life',
    horizon_years: 2,
    choices: [
      {
        name: 'Live alone',
        notes: 'Full privacy, full cost — utilities, rent, internet all yours.',
        variables: [
          v('Rent', 'cost', 'per_step', '/month', N(1600, 200)),
          v('Utilities + internet', 'cost', 'per_step', '/month', N(150, 30)),
          v('Wellbeing (privacy)', 'wellbeing', 'per_step', 'pts/10', N(8, 1)),
        ],
      },
      {
        name: 'Share with 2 roommates',
        notes: 'Shared costs, shared space — social upside or friction depending on fit.',
        variables: [
          v('Rent (your share)', 'cost', 'per_step', '/month', N(850, 120)),
          v('Utilities + internet (share)', 'cost', 'per_step', '/month', N(60, 15)),
          v('Wellbeing (social + privacy trade-off)', 'wellbeing', 'per_step', 'pts/10', N(7, 1.5)),
        ],
      },
    ],
  },
  {
    key: 'nomad',
    label: 'Go digital nomad for a year?',
    emoji: '✈️',
    description: 'Work remotely while travelling — model the real cost and wellbeing impact.',
    category: 'life',
    horizon_years: 2,
    choices: [
      {
        name: 'Stay put',
        notes: 'Stable base, lower logistics overhead, existing social network.',
        variables: [
          v('Living costs', 'cost', 'per_step', '/month', N(2200, 300)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(7, 1)),
        ],
      },
      {
        name: 'Go digital nomad',
        notes: 'Travel + work. Variable costs, tax complexity, productivity dips.',
        variables: [
          v('Nomad living costs (avg)', 'cost', 'per_step', '/month', N(2000, 600)),
          v('Setup + travel logistics', 'one_time_cost', 'once', '', N(3000, 1000)),
          v('Income lost to productivity dips', 'cost', 'per_step', '/year', LN(7.6, 0.8)),
          v('Tax / visa advice + complications', 'cost', 'per_step', '/year', LN(6.9, 0.8)),
          v('Wellbeing', 'wellbeing', 'per_step', 'pts/10', N(8.5, 2)),
        ],
      },
    ],
  },
]
